import { Icon } from '../icon'
import { IconButton, Button } from '../button'
import { useAccounts, useAccountBalances } from '../../lib/api/accounts'
import { useOrganizations } from '../../lib/api/organizations'
import { fmtMoneyShort } from '../../lib/money'
import { T } from '../../data/mock'
import type { Account, Currency } from '../../lib/api/types'
import { visibleNavItems, type NavId } from './top-bar'
import { useMe } from '../../lib/use-me'

// Currencies are shown as their own separate totals — never summed across
// currencies — and always in this order so the dominant currency (UAH) leads.
const CCY_ORDER: Currency[] = ['UAH', 'EUR', 'USD']
function orderedCcyTotals(
  totals: Partial<Record<Currency, number>>,
): Array<[Currency, number]> {
  return (Object.entries(totals) as Array<[Currency, number]>).sort(
    ([a], [b]) => CCY_ORDER.indexOf(a) - CCY_ORDER.indexOf(b),
  )
}

type Props = {
  selected: string
  onSelect: (id: string) => void
  onNavigate?: (id: NavId) => void
  onOpenMovement: (id: string) => void
  /** Highlights the current section in the mobile-only in-rail nav. */
  active?: NavId
}

/**
 * Section navigation rendered at the top of the rail. Hidden on desktop (the
 * top bar owns nav there) and shown only when the rail is an off-canvas mobile
 * panel — so phones get a single "menu" that holds both nav and accounts.
 */
function RailNav({ active, onNavigate }: { active?: NavId; onNavigate?: (id: NavId) => void }) {
  const { data: me } = useMe()
  const navItems = visibleNavItems(me?.role)
  return (
    <nav className="rail-nav">
      {navItems.map((it) => (
        <button
          key={it.id}
          type="button"
          className={`nav-item ${active === it.id ? 'active' : ''}`}
          onClick={() => onNavigate?.(it.id)}
        >
          <Icon name={it.icon} size={16} />
          <span>{it.label}</span>
        </button>
      ))}
    </nav>
  )
}

function AccountButton({
  account,
  amount,
  selected,
  onSelect,
  onOpenMovement,
}: {
  account: Account
  amount: number
  selected: boolean
  onSelect: (id: string) => void
  onOpenMovement: (id: string) => void
}) {
  return (
    // Stretched-link card: the whole card selects the account (.rail-acc-hit
    // covers it). Top row is the full account name; below it, the "Виписка"
    // action sits on the left and the balance (which already carries its
    // currency) on the right.
    <div className={`rail-acc ${selected ? 'selected' : ''} ${amount === 0 ? 'zero' : ''}`}>
      <button
        type="button"
        className="rail-acc-hit"
        onClick={() => onSelect(account.id)}
        aria-label={account.name}
      />
      <div className="rail-acc-title">{account.name}</div>
      <div className="rail-acc-foot-row">
        <button
          type="button"
          className="rail-acc-statement"
          onClick={(e) => {
            e.stopPropagation()
            onOpenMovement(account.id)
          }}
          title="Виписка"
        >
          Виписка
        </button>
        <span className="rail-acc-balance">{fmtMoneyShort(amount, account.currency)}</span>
      </div>
    </div>
  )
}

function OrgGroup({
  orgName,
  accounts,
  balancesByAccount,
  selected,
  onSelect,
  onOpenMovement,
}: {
  orgName: string
  accounts: Account[]
  balancesByAccount: Map<string, number>
  selected: string
  onSelect: (id: string) => void
  onOpenMovement: (id: string) => void
}) {
  const totalsByCcy = accounts.reduce<Partial<Record<Currency, number>>>((acc, a) => {
    const bal = balancesByAccount.get(a.id) ?? a.openingBalance
    acc[a.currency] = (acc[a.currency] ?? 0) + bal
    return acc
  }, {})
  // A single-account org's total just repeats that account's balance, so the
  // summary is only meaningful (and only shown) when there are 2+ accounts.
  // Each currency is its own line — currencies are never merged into one figure.
  const summaryLines =
    accounts.length > 1
      ? orderedCcyTotals(totalsByCcy).map(([ccy, total]) => fmtMoneyShort(total, ccy))
      : []

  return (
    <div className="rail-group">
      <div className="rail-group-head">
        <span className="rail-group-name">{orgName}</span>
        {summaryLines.length > 0 && (
          <span className="rail-group-total">
            {summaryLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </span>
        )}
      </div>
      {accounts.map((a) => (
        <AccountButton
          key={a.id}
          account={a}
          amount={balancesByAccount.get(a.id) ?? a.openingBalance}
          selected={selected === a.id}
          onSelect={onSelect}
          onOpenMovement={onOpenMovement}
        />
      ))}
    </div>
  )
}

export function AccountsRail({ selected, onSelect, onNavigate, onOpenMovement, active }: Props) {
  const organizations = useOrganizations()
  const accounts = useAccounts()
  const balances = useAccountBalances()
  const balancesByAccount = new Map(
    (balances.data ?? []).map((b) => [b.accountId, b.balance]),
  )

  // Archived accounts stay manageable in Settings but are hidden from the rail
  // and excluded from org/grand totals.
  const visibleAccounts = (accounts.data ?? []).filter((a) => !a.isArchived)

  // Hide organizations with nothing to show — no accounts at all, or every
  // account archived (archived ones are already excluded from visibleAccounts).
  const groups = (organizations.data ?? [])
    .map((org) => ({
      org,
      accounts: visibleAccounts.filter((a) => a.organizationId === org.id),
    }))
    .filter((g) => g.accounts.length > 0)

  const totalsByCcy = visibleAccounts.reduce<Partial<Record<Currency, number>>>(
    (acc, a) => {
      const bal = balancesByAccount.get(a.id) ?? a.openingBalance
      acc[a.currency] = (acc[a.currency] ?? 0) + bal
      return acc
    },
    {},
  )
  // Totals (and the per-account figures) are only trustworthy once the balances
  // query has actually succeeded. Until then we render placeholders instead of
  // falling back to openingBalance (which is 0 for most accounts) — otherwise a
  // slow or failed balances request would masquerade as real zero balances.
  const totalLines = balances.isSuccess
    ? orderedCcyTotals(totalsByCcy).map(([ccy, total]) => fmtMoneyShort(total, ccy))
    : []

  // Wait for balances too: without it the rail would paint accounts at their
  // openingBalance (≈ all zeros) for the window before the balances query lands.
  const isLoading = organizations.isLoading || accounts.isLoading || balances.isLoading
  const balancesFailed = !isLoading && balances.isError
  const hasNothing =
    !isLoading &&
    !balancesFailed &&
    groups.length === 0 &&
    (accounts.data?.length ?? 0) === 0

  return (
    <aside className="rail">
      <RailNav active={active} onNavigate={onNavigate} />
      <div className="rail-head">
        <span className="rail-head-title">{T.nav.accounts}</span>
        <div style={{ flex: 1 }} />
        <IconButton
          icon="plus"
          tooltip="Додати рахунок"
          onClick={() => onNavigate?.('settings')}
        />
      </div>
      <div
        className={`rail-total ${selected === 'all' ? 'selected' : ''}`}
        onClick={() => onSelect('all')}
      >
        <div className="rail-total-label">
          <Icon name="wallet" size={11} color="var(--fg-3)" />
          Усі рахунки
        </div>
        <div className="rail-total-amount">{totalLines[0] ?? '—'}</div>
        {totalLines.slice(1).map((line, i) => (
          <div className="rail-total-ccy" key={i}>
            {line}
          </div>
        ))}
      </div>
      <div className="rail-scroll">
        {isLoading && (
          <div className="rail-group" style={{ color: 'var(--fg-4)', padding: '12px' }}>
            Завантаження…
          </div>
        )}
        {balancesFailed && (
          <div className="rail-group" style={{ color: 'var(--fg-4)', padding: '12px' }}>
            Не вдалося завантажити залишки.{' '}
            <button
              type="button"
              onClick={() => balances.refetch()}
              style={{
                color: 'var(--accent-9)',
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: 'pointer',
              }}
            >
              Спробувати ще раз
            </button>
          </div>
        )}
        {hasNothing && (
          <div className="rail-group" style={{ color: 'var(--fg-4)', padding: '12px' }}>
            Створіть першу організацію та рахунок у{' '}
            <button
              type="button"
              onClick={() => onNavigate?.('settings')}
              style={{
                color: 'var(--accent-9)',
                background: 'transparent',
                border: 0,
                padding: 0,
                cursor: 'pointer',
              }}
            >
              Налаштуваннях
            </button>
          </div>
        )}
        {!isLoading &&
          !balancesFailed &&
          groups.map(({ org, accounts: orgAccs }) => (
          <OrgGroup
            key={org.id}
            orgName={org.name}
            accounts={orgAccs}
            balancesByAccount={balancesByAccount}
            selected={selected}
            onSelect={onSelect}
            onOpenMovement={onOpenMovement}
          />
        ))}
      </div>
      <div className="rail-foot">
        <Button
          variant="ghost"
          size="sm"
          icon="arrow-left-right"
          onClick={() => onNavigate?.('transactions')}
        >
          {T.acc.transfer}
        </Button>
        <div style={{ flex: 1 }} />
        <IconButton
          icon="settings-2"
          tooltip="Налаштувати рахунки"
          onClick={() => onNavigate?.('settings')}
        />
      </div>
    </aside>
  )
}
